import React from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { FormikProps } from 'formik';

import {
    ColumnDto,
    ColumnDtoTypeEnum,
    DataSourceDto,
    GroupByDtoDateTruncUnitEnum,
} from '../../../../../../../typescript-axios';
import { isFormFieldInvalid, getFormErrorMessage } from '../../../utils/form_utils';

interface StepGroupByProps {
    formik: FormikProps<{ dataSource: DataSourceDto }>;
    columnOptions: Record<string, ColumnDto>;
}

export const StepGroupBy: React.FC<StepGroupByProps> = ({ formik, columnOptions }) => {
    const { t } = useTranslation();

    const selectedAliases: string[] =
        formik.values.dataSource.query_definition.columns?.flatMap((col: ColumnDto) => {
            const match = Object.entries(columnOptions).find(
                ([, dto]) => dto.table === col.table && dto.column === col.column
            );
            return match ? [match[0]] : [];
        }) ?? [];

    const hasTimestampColumn = formik.values.dataSource.query_definition.columns?.some(
        (col: ColumnDto) => col.type === ColumnDtoTypeEnum.Timestamp
    );

    const handleColumnsChange = (selectedAliases: string[]) => {
        const newColumns =
            selectedAliases.length > 0
                ? selectedAliases.map((alias) => columnOptions[alias])
                : undefined;

        formik.setFieldValue('dataSource.query_definition.columns', newColumns);

        if (!newColumns) {
            formik.setFieldValue('dataSource.query_definition.group_by', undefined);
        } else {
            const currentTrunc = formik.values.dataSource.query_definition.group_by?.date_trunc_unit;
            formik.setFieldValue(
                'dataSource.query_definition.group_by',
                currentTrunc ? { date_trunc_unit: currentTrunc } : {}
            );
        }
    };

    const handleDateTruncChange = (unit: GroupByDtoDateTruncUnitEnum | undefined) => {
        formik.setFieldValue(
            'dataSource.query_definition.group_by',
            unit ? { date_trunc_unit: unit } : {}
        );
    };

    return (
        <div className="formgrid grid col-12">

            {/* Column MultiSelect */}
            <div className="field col-12">
                <label
                    htmlFor="input-create-datasource-group_by"
                    className="font-medium"
                >
                    {t('pages.dashboards.datasource.select_columns')}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <MultiSelect
                    id="input-create-datasource-select_columns"
                    name="select_columns"
                    value={selectedAliases}
                    options={Object.keys(columnOptions)}
                    onChange={(e) => handleColumnsChange(e.value)}
                    onBlur={() =>
                        formik.setFieldTouched('dataSource.query_definition.columns', true)
                    }
                    display="chip"
                    filter
                    className={
                        isFormFieldInvalid(
                            'dataSource.query_definition.columns',
                            formik.touched,
                            formik.errors
                        )
                            ? 'p-invalid w-full'
                            : 'w-full'
                    }
                />
                {isFormFieldInvalid(
                    'dataSource.query_definition.columns',
                    formik.touched,
                    formik.errors
                ) && (
                        <small className="p-error block mt-1">
                            {getFormErrorMessage(
                                'dataSource.query_definition.columns',
                                formik.touched,
                                formik.errors
                            )}
                        </small>
                    )}
            </div>

            {/* Date Trunc Unit — only shown when a timestamp column is selected */}
            {hasTimestampColumn && (
                <div className="field col-12">
                    <label
                        htmlFor="input-create-datasource-date_trunc_unit"
                        className="font-medium"
                    >
                        {t('pages.dashboards.datasource.select_date_trunc_unit')}
                    </label>
                    <Dropdown
                        id="input-create-datasource-date_trunc_unit"
                        name="date_trunc_unit"
                        value={formik.values.dataSource.query_definition.group_by?.date_trunc_unit}
                        options={Object.values(GroupByDtoDateTruncUnitEnum).map((unit) => ({
                            label: unit,
                            value: unit,
                        }))}
                        onChange={(e) => handleDateTruncChange(e.value ?? undefined)}
                        onBlur={() =>
                            formik.setFieldTouched(
                                'dataSource.query_definition.group_by.date_trunc_unit',
                                true
                            )
                        }
                        disabled={!formik.values.dataSource.query_definition.columns?.length}
                        showClear
                        className={
                            isFormFieldInvalid(
                                'dataSource.query_definition.group_by.date_trunc_unit',
                                formik.touched,
                                formik.errors
                            )
                                ? 'p-invalid w-full'
                                : 'w-full'
                        }
                    />
                    {isFormFieldInvalid(
                        'dataSource.query_definition.group_by.date_trunc_unit',
                        formik.touched,
                        formik.errors
                    ) && (
                            <small className="p-error block mt-1">
                                {getFormErrorMessage(
                                    'dataSource.query_definition.group_by.date_trunc_unit',
                                    formik.touched,
                                    formik.errors
                                )}
                            </small>
                        )}
                </div>
            )}
        </div>
    );
};